import {useContext, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {parsePhoneNumberFromString} from 'libphonenumber-js';
import {AxiosError} from 'axios';
import {colors} from 'patient-ping-remedy/packages/theme';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import Checkbox from 'patient-ping-remedy/packages/checkbox';
import Button from 'patient-ping-remedy/packages/button';
import Badge from 'patient-ping-remedy/packages/badge';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useActiveRoster, useRosterStore} from '@/store/roster_store';
import {useAlertStore} from '@/store/alert_store';
import {ConnectContactStatus, ConnectContactChannel, ConnectContactResponse} from '@/api/dto/connect';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import {useTimeSince} from '@/hooks/useTimeSince';
import AwsCommunicationsContactVoicemail
  from '@/components/pages/tasks/aws_communications/contact/AwsCommunicationsContactVoicemail';
import AwsCommunicationsContactSms from '@/components/pages/tasks/aws_communications/contact/AwsCommunicationsContactSms';
import AwsCommunicationAssociateModal from '@/components/pages/tasks/aws_communications/AwsCommunicationAssociateModal';
import AwsCommunicationsSmsConversationModal
  from '@/components/pages/tasks/aws_communications/AwsCommunicationsSmsConversationModal';
import CopyButton from '@common/CopyButton';
import DisplayHelpers from '@/helpers/display_helpers';
import Helpers from '@/helpers/helpers';
import {useMixpanel} from '@/hooks/useMixpanel';
import {MixpanelAwsCommunicationEvent, MixpanelEventType} from '@/api/dto/mixpanel';
import {Wrapper, Header, Content, ClientName, PhoneNumber, GroupAssociation, Body, TimeSince,
  TimeRow, ViewConversationLink}
  from '@/components/pages/tasks/aws_communications/contact/AwsCommunicationsContact.styles';

const channels: Record<ConnectContactChannel, { label: string, color: colors }> = {
  [ConnectContactChannel.VOICE]: {
    label: 'Missed call',
    color: colors.red1,
  },
  [ConnectContactChannel.VOICEMAIL]: {
    label: 'Voicemail',
    color: colors.teal1,
  },
  [ConnectContactChannel.SMS]: {
    label: 'SMS',
    color: colors.green1,
  },
  [ConnectContactChannel.EMAIL]: {
    label: 'Email',
    color: colors.purple1,
  },
};

type Props = {
  connectContact: ConnectContactResponse;
  rosterId?: number;
  interactable?: boolean;
  hideConversationLink?: boolean;
}

export default function AwsCommunicationsContact({
  connectContact,
  rosterId,
  interactable,
  hideConversationLink,
}: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const activeRoster = useActiveRoster();
  const selectedRoster = useRosterStore((state) => state.selectedRoster);
  const { addAlert } = useAlertStore();
  const { mixpanelEventProperties, getCurrentGroupDetails } = useMixpanel();
  const queryClient = useQueryClient();

  // Prefer the contact's own roster; otherwise use the roster resolved by the drawer (the "assumed"
  // roster for the cross-roster Tasks page, where useActiveRoster() is null -- see CARECO-5862 /
  // CARECO-5939). Falling back to useActiveRoster() keeps behavior intact for any caller that renders
  // this component with a live active roster and no explicit prop.
  const effectiveRosterId = connectContact.rosterId ?? rosterId ?? activeRoster?.id;

  // Analytics are attributed to the group the panel is showing, resolved the same way as
  // AwsCommunicationsDrawer: the active roster, else the group last selected on the Clients page.
  const panelRoster = activeRoster ?? selectedRoster;

  const isCompleted = connectContact.status === ConnectContactStatus.COMPLETED;
  const timeSince = useTimeSince(connectContact, isCompleted ? 'completedAt' : 'contactedAt');

  const [associateContactModalOpen, setAssociateContactModalOpen] = useState(false);
  const [conversationModalOpen, setConversationModalOpen] = useState(false);

  const { mutate: updateContactStatus, isPending } = useMutation({
    mutationFn: async (status: ConnectContactStatus) =>
      carecoApi?.patchConnectContactStatus(connectContact.id, effectiveRosterId!, status),
    onSuccess(_data, status) {
      if (status === ConnectContactStatus.COMPLETED && mixpanelEventProperties && panelRoster) {
        const event: MixpanelAwsCommunicationEvent = {
          eventName: MixpanelEventType.BIH_AWS_COMMUNICATION_COMPLETED,
          channel: connectContact.channel,
          ...mixpanelEventProperties,
          ...getCurrentGroupDetails(panelRoster)
        };
        carecoApi?.postMixpanelEvent(event);
      }
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Contact status updated successfully.',
      });
      void queryClient.invalidateQueries({ queryKey: ['connect', 'contacts'] });
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to update contact status. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const isEmail = connectContact.channel === ConnectContactChannel.EMAIL;
  const isSms = connectContact.channel === ConnectContactChannel.SMS;
  const portalBaseUrl = process.env.REACT_APP_SECURE_EMAIL_PORTAL_URL ?? '';

  const name = isEmail
    ? (connectContact.emailSender ?? 'Unknown sender')
    : DisplayHelpers.formatName(
      connectContact.client.firstName,
      connectContact.client.middleName,
      connectContact.client.lastName,
    );

  const nationalNumber = connectContact.phoneNumber
    ? parsePhoneNumberFromString(connectContact.phoneNumber)?.nationalNumber
    : undefined;
  const phoneNumber = DisplayHelpers.formatPhoneNumber(nationalNumber);
  const e164 = connectContact.phoneNumber
    ? DisplayHelpers.formatE164PhoneNumber(connectContact.phoneNumber)
    : undefined;

  return (
    <>
      <Wrapper data-communications-item>
        {interactable && !isEmail && (
          <Checkbox
            value={isPending !== isCompleted}
            disabled={isPending || !effectiveRosterId}
            onChange={() => updateContactStatus(isCompleted
              ? ConnectContactStatus.PENDING
              : ConnectContactStatus.COMPLETED)}
          />
        )}
        <Header>
          <ClientName isCompleted={isCompleted}>
            {name}
          </ClientName>
          <Badge backgroundColor={channels[connectContact.channel].color} textColor={colors.charcoal}>
            {isEmail
              ? (connectContact.emailNotificationReason === 'unread_reply' ? 'Unread reply' : 'Unassigned Email')
              : channels[connectContact.channel].label}
          </Badge>
        </Header>
        <div style={{ marginLeft: 'auto' }}>
          {interactable && !isEmail && !connectContact.client.publicPatientId && (
            <Button
              disabled={!effectiveRosterId}
              onClick={() => setAssociateContactModalOpen(true)}
            >
              Associate with client
            </Button>
          )}
          {isEmail && connectContact.emailThreadId && portalBaseUrl && (
            <Button onClick={() => window.open(`${portalBaseUrl}/threads/${connectContact.emailThreadId}`, '_blank')}>
              View
            </Button>
          )}
        </div>
        <div />
        <Content>
          {isEmail ? (
            <PhoneNumber isCompleted={isCompleted} style={{ display: 'block', maxWidth: '20rem' }}>
              {connectContact.emailSubject ?? 'No subject'}
            </PhoneNumber>
          ) : (
            <PhoneNumber isCompleted={isCompleted}>
              {phoneNumber}
              {e164 && (
                <CopyButton
                  value={e164}
                  ariaLabel="Copy phone number"
                  errorMessage="Unable to copy phone number. Your browser may not support copying."
                />
              )}
            </PhoneNumber>
          )}
          {!connectContact.rosterId && (
            <GroupAssociation>
              Group could not be associated
            </GroupAssociation>
          )}
          <Body>
            {connectContact.channel === ConnectContactChannel.VOICEMAIL
              ? <AwsCommunicationsContactVoicemail connectContact={connectContact} rosterId={effectiveRosterId} />
              : connectContact.channel === ConnectContactChannel.SMS
                ? <AwsCommunicationsContactSms connectContact={connectContact} />
                : null}
          </Body>
          <TimeRow>
            <TimeSince>
              <Icon iconClass={ICONS.clock} weight="far" />
              {isCompleted && 'Completed'} {timeSince}
            </TimeSince>
            {isSms && !hideConversationLink && (
              <ViewConversationLink onClick={() => setConversationModalOpen(true)}>
                View conversation
              </ViewConversationLink>
            )}
          </TimeRow>
        </Content>
      </Wrapper>
      {!isEmail && (
        <AwsCommunicationAssociateModal
          isOpen={associateContactModalOpen}
          close={() => setAssociateContactModalOpen(false)}
          connectContact={connectContact}
          rosterId={effectiveRosterId}
        />
      )}
      {isSms && !hideConversationLink && (
        <AwsCommunicationsSmsConversationModal
          isOpen={conversationModalOpen}
          close={() => setConversationModalOpen(false)}
          connectContact={connectContact}
        />
      )}
    </>
  );
}
